import { createClient } from "@supabase/supabase-js";
import { nanoid } from "nanoid";

const supabase = createClient("https://igalgzepbejgggxvyder.supabase.co", "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlnYWxnemVwYmVqZ2dneHZ5ZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MTk1MjM5NzcsImV4cCI6MjAzNTA5OTk3N30.spwnsmuetBcgN4DyAyTxHpUtIVzEgNyF6KvJnZkldf4");

// Storage `list()` returns at most one page per call (100 entries by default), so a
// project with more uploads than that needs every page walked before it is complete.
const LIST_PAGE_SIZE = 100

// Storage has no concept of upload order, so uploaded files carry a zero-padded sequence
// prefix and the stored name itself records the order. Files uploaded before stamping
// existed fall back to `created_at`, the best ordering signal available for them.
const UPLOAD_STAMP_PATTERN = /^(\d{4,})__/
const UPLOAD_STAMP_DIGITS = 4

export const parseUploadStamp = (name: string) => {
    const match = UPLOAD_STAMP_PATTERN.exec(name)
    return match ? Number(match[1]) : null
}

export const buildStampedName = (stamp: number, originalName: string) =>
    `${String(stamp).padStart(UPLOAD_STAMP_DIGITS, "0")}__${originalName}`

// Continues the sequence rather than restarting it, so a later batch lands after the
// files already stored instead of interleaving with them.
export const nextUploadStamp = (files: any[]) => {
    let highest = 0

    files.forEach((file) => {
      const stamp = parseUploadStamp(file.name)
      if (stamp !== null && stamp > highest) {
        highest = stamp
      }
    })

    return highest + 1
}

// Stamped files know their exact order. Unstamped ones predate stamping, so they sort
// first, by upload time — a project part-way through the change stays chronological.
const compareUploadOrder = (a: any, b: any) => {
    const stampA = parseUploadStamp(a.name)
    const stampB = parseUploadStamp(b.name)

    if (stampA !== null && stampB !== null) {
      return stampA - stampB
    }
    if (stampA !== null) {
      return 1
    }
    if (stampB !== null) {
      return -1
    }

    const createdA = Date.parse(a.created_at) || 0
    const createdB = Date.parse(b.created_at) || 0
    if (createdA !== createdB) {
      return createdA - createdB
    }

    return String(a.name).localeCompare(String(b.name))
}

export const listAllFiles = async (path: string, client: any = supabase) => {
    const files = []
    let offset = 0

    for (;;) {
      const { data, error } = await client
        .storage
        .from('images')
        .list(path, {
          limit: LIST_PAGE_SIZE,
          offset,
          // Name is unique, so this keeps offset paging from skipping or repeating
          // entries. Upload order is applied once every page is in hand.
          sortBy: { column: 'name', order: 'asc' },
        })

      if (error) {
        throw error
      }

      if (!data || data.length === 0) {
        break
      }

      files.push(...data)
      offset += data.length

      // A short page means storage has nothing left to hand back.
      if (data.length < LIST_PAGE_SIZE) {
        break
      }
    }

    return files.sort(compareUploadOrder)
}

export const listImages = async (projectId: string) => {
    let data
    try {
      data = await listAllFiles(projectId)
    } catch (error) {
      // Handle error
      console.error("Error listing images",error)
      return
    }
  
    let imageulrs = []
    //call getPublicUrl for each image
    data?.forEach((image) => {
      let res = supabase
        .storage
        .from('images')
        .getPublicUrl(`${projectId}/${image.name}`)
      
      imageulrs.push({
        id: nanoid(),
        src: res.data.publicUrl,
        preview: res.data.publicUrl,
        type: "StaticImage",
      })
  
    })
    
    return imageulrs
}

export const listImagesAtPath = async (path: string) => {
    let data
    try {
      data = await listAllFiles(path)
    } catch (error) {
      console.error("Error listing images",error)
      return
    }

    const imageUrls = []
    data?.forEach((image) => {
      const res = supabase
        .storage
        .from('images')
        .getPublicUrl(`${path}/${image.name}`)

      imageUrls.push({
        id: nanoid(),
        src: res.data.publicUrl,
        preview: res.data.publicUrl,
        type: "StaticImage",
      })
    })

    return imageUrls
}

export const getSkuData = async (sku?: string) => {
    
    let { data, error } = await supabase
      .from('product')
      .select('*')
      .eq('sku', sku || 'SCS-24680')
      .single()
  
    if (error) {
      // Handle error
      console.error("Error getting sku data",error)
      return
    }
  
    return data
}

export const getSavedProject = async (designId: string, customerId: string) => {
    let { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('design_id', designId)
      .eq('customer_id', customerId)
      .single()
  
    if (error) {
      // Handle error
      console.error("Error getting saved project",error)
      return
    }
  
    return data
}

export const getProjectForDownload = async (id: string) => {
  let { data, error } = await supabase
    .from('order_item')
    .select('design')
    .eq('order_item_id', id)
    .single()

  if (error) {
    // Handle error
    console.error("Error getting saved project",error)
    return
  }

  return data
}

export const getPremiumProject = async (id: string) => {
  let { data, error } = await supabase
    .from('order_item')
    .select('order_item_id, design, premium_design_id, premium_request, product_sku')
    .eq('order_item_id', id)
    .single()

  if (error) {
    console.error("Error getting premium project",error)
    return
  }

  return data
}

export const setCartItemDesign = async (uuid: string, design: any) => {
    let { data, error } = await supabase
      .from('cart_item')
      .update({ design: design })
      .eq('uuid', uuid)
  
    if (error) {
      // Handle error
      console.error("Error setting cart item design",error)
      return
    }
  
    return data
}
  
export const uploadFile = async (projectId: string, file: File) => {
    const { data, error } = await supabase.storage.from('images').upload(projectId, file)
    if (error) {
      // Handle error
      console.error("Error uploading",error)
      return
    } else {
      // Handle success
      return data
    }
}
